/**
 * RTWO Native Currency Engine — Native Currency Event Publisher Service
 */

import type {
  CurrencyId,
  EventId,
  NATVEventHandler,
  NATVEventType,
  RTWONativeCurrencyEvent,
  RTWONativeCurrencyEventType,
} from '../domain-models';
import type {
  INativeCurrencyEventPublisherService,
  NativeCurrencyEventPublishResult,
} from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { EventBusAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class NativeCurrencyEventPublisherService
  extends BaseNATVService
  implements INativeCurrencyEventPublisherService
{
  readonly serviceId = 'natv-event-publisher-001';
  readonly serviceName = 'Native Currency Event Publisher Service';
  readonly description =
    'Native currency event publisher — placeholder, requires Event Bus connection. No fake events published.';
  readonly integrations = ['Core Engine', 'Event Bus', 'Indexer Engine'];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = false;

  private readonly handlers = new Map<NATVEventType, NATVEventHandler[]>();

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly eventBusRegistry: EventBusAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async publishEvent(
    currencyId: CurrencyId,
    eventType: RTWONativeCurrencyEventType,
    payloadReference: string,
    sourceModule: string
  ): Promise<NativeCurrencyEventPublishResult> {
    if (!this.eventBusRegistry.isConfigured()) {
      return {
        success: false,
        eventId: null,
        error: 'Event publishing requires Event Bus connection — Event Bus Not Connected',
        isPlaceholder: true,
        source: 'Native Currency Engine — Event Bus Not Connected',
      };
    }

    const adapterResult = await this.eventBusRegistry
      .getActiveAdapter()
      .publishNativeCurrencyEvent(currencyId, eventType, payloadReference, sourceModule);

    if (adapterResult?.event) {
      this.repositories.events.save(adapterResult.event);
      this.context.eventBus.emit(NATVInternalEventTypes.NATIVE_CURRENCY_EVENT_PUBLISHED, {
        eventId: adapterResult.event.eventId,
        currencyId,
      });
    }

    return {
      success: false,
      eventId: null,
      error: 'Event publishing requires Event Bus connection — Event Bus Not Connected',
      isPlaceholder: true,
      source: 'Native Currency Engine — Event Bus Not Connected',
    };
  }

  getAllEvents(): RTWONativeCurrencyEvent[] {
    if (!this.eventBusRegistry.isConfigured()) return [];
    return this.repositories.events.findAll();
  }

  getEventsByCurrencyId(currencyId: CurrencyId): RTWONativeCurrencyEvent[] {
    if (!this.eventBusRegistry.isConfigured()) return [];
    return this.repositories.events.findByCurrencyId(currencyId);
  }

  getEvent(eventId: EventId): RTWONativeCurrencyEvent | null {
    if (!this.eventBusRegistry.isConfigured()) return null;
    return this.repositories.events.findById(eventId);
  }

  subscribe(eventType: NATVEventType, handler: NATVEventHandler): () => void {
    const existing = this.handlers.get(eventType) ?? [];
    this.handlers.set(eventType, [...existing, handler]);

    return () => {
      const current = this.handlers.get(eventType) ?? [];
      this.handlers.set(
        eventType,
        current.filter((entry) => entry !== handler)
      );
    };
  }
}
